import { useCallback, useEffect, useState } from "react";
import { Check } from "lucide-react";
import { formatApiError } from "@/lib/api";
import { usePortal } from "./PortalContext";
import { MessageTagButton } from "./MessageTagButton";

export default function PortalTeam() {
  const portal = usePortal();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    try {
      const response = await portal.get("/team");
      setData(response.data);
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }, [portal]);
  useEffect(() => { load(); }, [load]);
  if (error) return <p className="cp-error">{error}</p>;
  if (!data) return <div className="cp-loading">Loading…</div>;
  const clientName = portal.account?.client?.name || "your organization";
  return (
    <div className="cp-page" data-testid="portal-team">
      <div className="cp-page-head">
        <div>
          <h1 className="cp-h1">Your TAG team</h1>
          <p className="cp-lede">Everyone on the Connecteam schedule for {clientName}, plus the evaluators working your open requests — with published days and hours.</p>
        </div>
        <span className="cp-count-pill" data-testid="portal-team-count"><b>{data.assigned} on your team</b> · {data.on_site_today} scheduled on site today</span>
      </div>
      <section className="cp-contact-bar" data-testid="portal-team-contact">
        <div>
          <p className="cp-eyebrow">Your TAG contact</p>
          <b>{data.tag_contact.name}</b> · <span>{data.tag_contact.email}</span>
        </div>
        <MessageTagButton className="cp-btn cp-btn-primary" label="Message TAG" testId="portal-team-message" />
      </section>
      {data.groups.length === 0 && <section className="cp-card"><p className="cp-muted" data-testid="portal-team-empty">No TAG staff are scheduled at {clientName} right now.</p></section>}
      {data.groups.map((group) => (
        <section key={group.key} className="cp-card" data-testid={`portal-team-group-${group.key}`}>
          <h2>{group.label}</h2>
          <ul className="cp-team-list">
            {group.members.map((member) => (
              <li key={member.id} data-testid={`portal-team-member-${member.id}`}>
                <span className="cp-avatar" aria-hidden="true">{member.initials}</span>
                <div className="cp-team-name">
                  <b data-testid={`portal-team-name-${member.id}`}>{member.name}</b>
                  <span data-testid={`portal-team-role-${member.id}`}>{member.role_label}</span>
                </div>
                <div className="cp-team-schedule">
                  <span data-testid={`portal-team-schedule-${member.id}`}>{member.schedule}</span>
                  {member.published_days?.length > 0 && (
                    <span className="cp-team-days" aria-label="Published days">
                      {["Mon", "Tue", "Wed", "Thu", "Fri"].map((day) => <i key={day} className={member.published_days.includes(day) ? "on" : ""}>{day[0]}</i>)}
                      {member.hours_label && <small>{member.hours_label}</small>}
                    </span>
                  )}
                  {member.evaluations_label && <small data-testid={`portal-team-evals-${member.id}`}>{member.evaluations_label}</small>}
                  {member.start_label && <small>Started {member.start_label}</small>}
                </div>
                <div className="cp-team-flags">
                  {member.credentials_verified && <em className="cp-verified" data-testid={`portal-team-verified-${member.id}`}><Check size={13} /> Credentials verified</em>}
                  {member.out_today && <em className="cp-out-today" data-testid={`portal-team-out-${member.id}`}>Out today</em>}
                </div>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
